<template>
  <tr
    v-if="bluredOrNotHidden"
    :class="{ active: isActive, hovered: showActions }"
    class="draggableBlock"
    data-block="Text"
    @mouseover="mouseOver"
    @mouseleave="mouseOut"
    @focusout="focusOut"
    @click.stop="() => click()"
  >
    <td
      :class="{
        'block-borders': showDragBorder,
        'dragged-opacity': isCurrentElementDragged,
      }"
      style="word-break: break-word; font-size: 0px"
      align="left"
      :style="{ backgroundColor: bgColor }"
    >
      <blockActions
        v-if="showActions"
        :column-padding-x="columnPaddingX"
        :column-width="columnWidth"
        :disable-up-movement="disableUpMovement"
        :disable-down-movement="disableDownMovement"
        :block-height="blockHeight"
        :block-width="blockWidth"
        :is-active="isActive"
        :is-text="true"
        :is-last-column="isLastColumn"
        :section-index="sectionIndex"
        :column-index="columnIndex"
        :block-index="blockIndex"
        :show-paste="showPaste"
        :copied="copied"
        @duplicate="copyBlock"
        @remove="deleteBlock"
        @moveUp="moveUp"
        @moveDown="moveDown"
        @enrich="enrich"
        @copy="copyToClipboard"
      ></blockActions>
      <div :id="id" class="relative" :style="{ ...divStyle, ...hiddenStyle }">
        <div
          v-if="aiGenerationLoading"
          class="absolute z-30 flex h-full w-full items-center justify-center bg-white opacity-60"
        >
          <div class="light-theme">
            <TLoadingSpinner :large="true"></TLoadingSpinner>
          </div>
        </div>
        <inline-text-editor
          ref="editor"
          :key="simulatedHover.toString()"
          v-model="blockContent"
          root-block="p"
          :context-menu="'link'"
          :editor-style="editorStyle"
          is-text
          :select-all="simulatedHover"
        ></inline-text-editor>
      </div>
    </td>
  </tr>
</template>

<script lang="ts" setup>
  import blockProperties from "@/composables/useBlockProperties";
  import InlineTextEditor from "./elements/InlineTextEditor.vue";
  import blockActions from "./blockActions.vue";
  import { TopolTextBlock } from "@/schemas/template/blocks/textBlockSchema";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useAiTextStore } from "@/stores/aiTextStore";

  import { TLoadingSpinner } from "@topol/twig-ui";
  import { useI18n } from "vue-i18n";
  import { computed, ref } from "vue";

  const props = defineProps<{
    block: TopolTextBlock;
    columnIndex: number;
    sectionIndex: number;
    blockIndex: number;
    isLocked: boolean;
  }>();

  const {
    showActions,
    showPaste,
    simulatedHover,
    blockHeight,
    blockWidth,
    id,
    bluredOrNotHidden,
    hiddenStyle,
    isActive,
    disableUpMovement,
    disableDownMovement,
    columnPaddingX,
    columnWidth,
    isLastColumn,
    isCurrentElementDragged,
    showDragBorder,
    copied,
    mouseOver,
    mouseOut,
    click,
    copyBlock,
    deleteBlock,
    moveUp,
    moveDown,
    copyToClipboard,
  } = blockProperties(props);

  const isEmpty = ref(false);

  const mjmlStore = useMjmlStore();
  const aiTextStore = useAiTextStore();
  const { t } = useI18n();

  const aiGenerationLoading = computed(() => {
    return aiTextStore.loading && aiTextStore.blockId === props.block.uid;
  });

  const lineHeight = computed(() => {
    if (props.block.attributes["line-height"]) {
      return props.block.attributes["line-height"];
    } else {
      return mjmlStore.getBaseAttrString("mj-text", "line-height");
    }
  });

  const padding = computed(() => {
    if (
      props.block.attributes["padding"] !== null ||
      props.block.attributes["padding"] !== undefined
    ) {
      return props.block.attributes["padding"];
    }
    return "10px 25px";
  });

  const divStyle = computed(() => {
    return {
      fontSize: props.block.attributes["font-size"] || "13px",
      fontFamily:
        props.block.attributes["font-family"] || "Helvetica, Arial, sans-serif",
    };
  });

  const editorStyle = computed(() => {
    return {
      padding: padding.value,
      lineHeight: lineHeight.value,
      color: props.block.attributes["color"] || "#000000",
      textAlign: props.block.attributes["align"],
    };
  });

  const blockContent = computed({
    get() {
      if (
        isEmpty.value &&
        mjmlStore.mjml.langs &&
        mjmlStore.mjml.langs.length > 1
      ) {
        return "";
      }
      const content = mjmlStore.getSpecificBlockContent(props.block);
      return content
        ? content
            .replace("<!--[CDATA[", "")
            .replace("]]-->", "")
            .replace("/", "/")
        : "";
    },
    set(val: string) {
      if (blockContent.value === val) return;
      if (val === null || val === undefined || val === "") {
        isEmpty.value = true;
      } else {
        isEmpty.value = false;
      }

      mjmlStore.updateSpecificBlockProp({
        prop: "content",
        value: val,
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: props.blockIndex,
        uid: props.block.uid,
      });
    },
  });

  const bgColor = computed(() => {
    const bgColor = props.block.attributes["container-background-color"];
    if (bgColor !== null || bgColor !== undefined) {
      return bgColor;
    }

    return "";
  });

  function focusOut() {
    if (isEmpty.value) {
      const langs = mjmlStore.mjml.langs;
      if (
        langs &&
        langs.length > 1 &&
        !langs.find((l) => l.key === mjmlStore.langToShow)?.primary
      ) {
        isEmpty.value = false;
        return;
      }
      mjmlStore.updateSpecificBlockProp({
        prop: "content",
        value: t("layout.default_text"),
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: props.blockIndex,
      });
      isEmpty.value = false;
    }
  }

  function enrich() {
    aiTextStore.sectionIndex = props.sectionIndex;
    aiTextStore.columnIndex = props.columnIndex;
    aiTextStore.blockIndex = props.blockIndex;
    aiTextStore.blockId = props.block.uid;
    aiTextStore.setPromptText(blockContent.value);

    // if (openDialog) {
    //   this.dialogStore.addAiTextDialog.opened = true;
    // }
  }
</script>

<style>
  p {
    margin: 0;
  }

  .tox {
    z-index: 1600;
  }

  .tox-tinymce-aux {
    z-index: 1600 !important;
  }

  .tox-dialog-wrap__backdrop {
    z-index: 1601 !important;
  }

  .tox-dialog {
    z-index: 1602 !important;
  }
</style>
